import { object, string } from "yup";
import countries from "src/utils/constants/countries/countries.json";
import { isIban, isSwiftCode } from "src/pages/LeanDeposit/tools/helper";

/**
 * One bank account, asked for once.
 *
 * There were two forms for this record and they disagreed about almost
 * everything. `LeanDeposit/ManualBank` — the one Settings, Edit bank and both
 * transfer drawers open — asked for six fields, validated the IBAN and the
 * Swift code against their formats, and required all six. `Deposit/Modals/
 * addBank` — the one the Deposit page opens — asked for NINE in a three-across
 * grid, spelled "(Required)" into three of its label strings, and validated
 * nothing at all: no IBAN check, no Swift check, and everything optional
 * except the bank's name, city and country.
 *
 * Same endpoint, same record, two different questions and two different
 * standards. And `Settings/EditBank` displays all nine, so the six-field form
 * could only ever fill two thirds of what the product then shows back.
 *
 * This is the union of the fields with the stricter form's rules. Six are
 * required because six are what it takes to send someone money; the routing
 * code and the two addresses stay optional, as they already were, because a
 * great many banks outside the US have no ABA at all.
 */
export const bankAccountInitialValues = {
  accountName: "",
  iban: "",
  bankName: "",
  bankNumber: "",
  branchCity: "",
  remittanceCountry: countries[0]?.value,
  address: "",
  aba: "",
  branchLocation: "",
};

export const bankAccountSchema = object().shape({
  /* Every required rule in the six-field form said the literal word
     "required", so an empty field answered with a lowercase adjective rather
     than with what to do about it. */
  accountName: string().required("Enter the name on the account"),
  bankName: string()
    .min(3, "Must be 3 characters or more")
    .max(30, "Must be 30 characters or less")
    .required("Enter the bank's name"),
  branchCity: string()
    .min(3, "Must be 3 characters or more")
    .max(30, "Must be 30 characters or less")
    .required("Enter the bank's city"),
  remittanceCountry: string().required("Choose a country"),
  bankNumber: string()
    .matches(isSwiftCode, "That is not a valid Swift code")
    .required("Enter the bank's Swift code"),
  iban: string()
    .matches(isIban, "That is not a valid IBAN")
    .required("Enter the account number or IBAN"),
  /* Optional in both forms before, and still optional. */
  address: string(),
  aba: string(),
  branchLocation: string(),
});
